import MarkdownIt from "markdown-it";

export type TextEditorFormat = "bold" | "italic" | "bulletList" | "orderedList";

// CommonMark with only these formats, every newline a break and HTML escaped; the composer must match.
const createParser = (formats: TextEditorFormat[]) => {
  const rules = ["escape", "newline"];
  if (formats.includes("bulletList") || formats.includes("orderedList")) {
    rules.push("list");
  }
  if (formats.includes("bold") || formats.includes("italic")) {
    rules.push("emphasis");
  }
  const parser = new MarkdownIt("zero", { breaks: true }).enable(rules);
  // No newline after a break: the editor (preserving whitespace) would read it as a second one.
  parser.renderer.rules.hardbreak = () => "<br>";
  parser.renderer.rules.softbreak = () => "<br>";
  return parser;
};

const parsers = new Map<string, MarkdownIt>();

const parserFor = (formats: TextEditorFormat[]) => {
  const key = [...formats].sort().join(",");
  let parser = parsers.get(key);
  if (!parser) {
    parser = createParser(formats);
    parsers.set(key, parser);
  }
  return parser;
};

// The parser's tokens, for drawing a value other than as HTML.
export const restrictedTokens = (
  markdown: string,
  formats: TextEditorFormat[]
) => parserFor(formats).parse(markdown, {});

const NESTED = "ul ul, ul ol, ol ul, ol ol";
const BLOCKS = ["P", "UL", "OL"];

// What an item holds after its nested list, as blocks: a run of text becomes a paragraph.
const blocksAfter = (list: Element): Element[] => {
  const blocks: Element[] = [];
  let run: Element | undefined;
  for (let node = list.nextSibling; node; ) {
    const next = node.nextSibling;
    if (node instanceof Element && BLOCKS.includes(node.tagName)) {
      blocks.push(node);
      run = undefined;
    } else if (run) {
      run.append(node);
    } else if (node.textContent?.trim()) {
      run = list.ownerDocument.createElement("p");
      run.append(node);
      blocks.push(run);
    }
    node = next;
  }
  return blocks;
};

// The parser sets a nested list on lines of its own; the item keeps no newline once it moves
// out, which the editor (preserving whitespace) would read as a paragraph of its own.
const trimEnd = (item: Element) => {
  for (let last = item.lastChild; last?.nodeType === Node.TEXT_NODE; ) {
    const previous = last.previousSibling;
    const text = (last.textContent ?? "").trimEnd();
    if (text) {
      last.textContent = text;
      return;
    }
    last.remove();
    last = previous;
  }
};

// Moves the outermost nested list to the top level, as the composer reads it: its items keep
// their own kind and numbering, so the list around it splits there and the rest numbers on. A
// bullet list in a bullet list joins it. What its item held after it follows, unmarked.
const liftList = (list: Element) => {
  const holder = list.parentElement as Element;
  const item = holder.tagName === "LI" ? holder : list;
  const outer = item.parentElement as Element;
  const joins = outer.tagName === "UL" && list.tagName === "UL";
  const trailing = item === holder ? blocksAfter(list) : [];
  // A joined list's items sit right after its item, and the split falls after the last of them.
  const joined = joins ? Array.from(list.children) : [];
  if (joins) {
    item.after(...joined);
    list.remove();
  }
  if (item === holder) {
    list.remove();
    trailing.forEach((block) => block.remove());
    trimEnd(holder);
  }
  if (joins && trailing.length === 0) return;
  const children = Array.from(outer.children);
  const at = children.indexOf(joined[joined.length - 1] ?? item);
  const after = outer.cloneNode(false) as Element;
  after.append(...children.slice(at + 1));
  if (outer.tagName === "OL") {
    const before = children
      .slice(0, at + 1)
      .filter((child) => child.tagName === "LI" && child !== list).length;
    after.setAttribute(
      "start",
      String(Number(outer.getAttribute("start") || 1) + before)
    );
  }
  outer.after(
    ...(joins ? [] : [list]),
    ...trailing,
    ...(after.children.length ? [after] : [])
  );
  if (outer.children.length === 0) outer.remove();
};

// Lists one level deep, read as the composer reads a nested one.
export const flattenNestedLists = (html: string): string => {
  if (!/<(ul|ol)\b/i.test(html)) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  if (!doc.body.querySelector(NESTED)) return html;
  for (
    let list = doc.body.querySelector(NESTED);
    list;
    list = doc.body.querySelector(NESTED)
  ) {
    liftList(list);
  }
  // A split leaves the parser's newlines between items at a list's end, where the editor
  // (preserving whitespace) would read them as an item of their own.
  doc.body.querySelectorAll("ul, ol").forEach((each) =>
    Array.from(each.childNodes)
      .filter(
        (node) => node.nodeType === Node.TEXT_NODE && !node.textContent?.trim()
      )
      .forEach((node) => node.remove())
  );
  return doc.body.innerHTML;
};

// Lists come out one level deep, as the editor holds them and the composer draws them.
export const restrictedMarkdownToHtml = (
  markdown: string | null | undefined,
  formats: TextEditorFormat[]
): string =>
  markdown ? flattenNestedLists(parserFor(formats).render(markdown)) : "";
